import type { ParentLinkSchema } from '@repo/schemas/parent/parentLink';
import type { UserPublic } from '@repo/types/users';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ParentKey } from './keys';
import { ParentService } from './parentService';

export function useUnlinkParent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ParentLinkSchema) => ParentService.unlinkParent(data).then((response) => response.data),
    onMutate: async (variables) => {
      const studentKey = [ParentKey.STUDENT_PARENTS, variables.studentId];
      const parentKey = [ParentKey.PARENT_CHILDREN, variables.parentId];

      await Promise.all([queryClient.cancelQueries({ queryKey: studentKey }), queryClient.cancelQueries({ queryKey: parentKey })]);

      const previousStudentParents = queryClient.getQueryData<UserPublic[]>(studentKey);
      const previousParentChildren = queryClient.getQueryData<UserPublic[]>(parentKey);

      queryClient.setQueryData<UserPublic[]>(studentKey, (prev) => prev?.filter((p) => p.id !== variables.parentId) ?? []);
      queryClient.setQueryData<UserPublic[]>(parentKey, (prev) => prev?.filter((c) => c.id !== variables.studentId) ?? []);

      return { previousStudentParents, previousParentChildren };
    },
    onError: (_error, variables, context) => {
      if (context?.previousStudentParents) {
        queryClient.setQueryData<UserPublic[]>([ParentKey.STUDENT_PARENTS, variables.studentId], context.previousStudentParents);
      }
      if (context?.previousParentChildren) {
        queryClient.setQueryData<UserPublic[]>([ParentKey.PARENT_CHILDREN, variables.parentId], context.previousParentChildren);
      }
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [ParentKey.STUDENT_PARENTS, variables.studentId],
      });
      void queryClient.invalidateQueries({
        queryKey: [ParentKey.PARENT_CHILDREN, variables.parentId],
      });
    },
  });
}
